import React, { FC, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { format, isBefore, isSameYear, isSameMonth } from 'date-fns';
import { Pagination } from '../pagination/Pagination';
import { LinkLabel } from '../linkLabel';
import { Image } from '../Image/Image';
import { Typography } from '../typography';
import { Select } from '../form/Select/Select';
import HtmlContent from '../htmlContent/HtmlContent';
import { Spacer } from '../spacer';

// Interface

export interface TradeShowsProps {
  tradeShows?: TradeShowProps[] | [];
  length?: number;
}

export interface TradeShowProps {
  startDate: Date;
  endDate: Date;
  length: number;
  id: string | number;
  tradeShowLink: string;
  logo: any;
  description: string;
  name: string;
  location: string;
}

// Styles

const StyledGrid = styled.div`
  ${({ theme: { space, media } }) => css`
    display: grid;
    gap: ${space.medium};

    .gatsby-image-wrapper-constrained {
      width: 100%;
    }

    @media (min-width: ${media.medium}) {
      grid-template-columns: ${`repeat(2, 1fr)`};
    }
  `};
`;

const StyledTradeShows = styled(Spacer)<TradeShowsProps>`
  ${({ theme: { space, media } }) => css``};
`;

const StyledTradeShow = styled.div<TradeShowsProps>`
  ${({ theme: { media, space } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.small};

    @media (min-width: ${media.medium}) {
      flex-direction: row;
    }
  `};
`;

const StyledImageWrapper = styled.div<TradeShowsProps>`
  ${() => css`
    flex: 1;
  `};
`;

const StyledContent = styled.div<TradeShowsProps>`
  ${({ theme: { space } }) => css`
    flex: 2;
    display: flex;
    flex-direction: column;
    gap: ${space.xSmall};
  `};
`;

const StyledLinkLabel = styled(LinkLabel)`
  ${() => css`
    display: inline;
  `};
`;

const StyledFilters = styled.div`
  ${({ theme: { media, space } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      flex-direction: row;
      justify-content: space-between;
    }
  `};
`;

const StyledPastFutureFilters = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    gap: ${space.xSmall};
  `};
`;

const StyledDateFilters = styled.div`
  ${({ theme: { media } }) => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    @media (min-width: ${media.medium}) {
      align-items: center;
    }
  `};
`;

const StyledDateFiltersTitle = styled.div`
  ${({ theme: { media, space } }) => css`
    display: none;

    @media (min-width: ${media.medium}) {
      display: inline-block;
      margin-right: ${space.small};
    }
  `};
`;

const StyledSelectYear = styled(Select)`
  ${({ theme: { colors } }) => css`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-color: ${colors.brandOne100};
    flex: 1;
    min-width: 100px;
  `};
`;

const StyledSelectMonth = styled(Select)`
  ${({ theme: { colors } }) => css`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: 0;
    border-color: ${colors.brandOne100};
    flex: 1;
  `};
`;

const createPages = (
  tradeShows: TradeShowsProps,
  pageSize: number
): TradeShowsProps[] => {
  if (!tradeShows?.length || tradeShows.length <= 0 || pageSize <= 0) return [];

  const pages: TradeShowsProps[] = [];
  for (let i = 0; i < tradeShows.length; i += pageSize) {
    pages.push(tradeShows.slice(i, i + pageSize));
  }

  return pages;
};

// JSX

export const TradeShows: FC<TradeShowsProps> = ({ tradeShows }) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [year, setYear] = useState<number | null>(null);
  const [month, setMonth] = useState<number | null>(null);
  const [tradeShowsFilteredSorted, setTradeShowsFilteredSorted] =
    useState<TradeShowsProps>(tradeShows);
  const [years, setYears] = useState<{ label: string; value: string }[]>([]);

  const today = new Date();

  const scrollToRef = useRef(null);

  useEffect(() => {
    // Gets all the Years from the shows and spits out unique years for the Year filter select
    const allDates = [];

    for (let i = 0; i < tradeShows.length; i++) {
      if (tradeShows[i]?.startDate) {
        allDates.push(format(new Date(tradeShows[i]?.startDate), 'yyyy'));
      }
    }

    const uniqueYears = [...new Set(allDates)];
    const optionMapping = [];

    for (let i = 0; i < uniqueYears.length; i++) {
      optionMapping.push({ label: uniqueYears[i], value: uniqueYears[i] });
    }

    setYears(optionMapping);
  }, [tradeShows]);

  useEffect(() => {
    if (!tradeShows) return null;
    if (!year && !month) return setTradeShowsFilteredSorted(tradeShows);

    let filteredShows = tradeShows.filter((show: TradeShowProps) =>
      isSameYear(new Date(show?.startDate), new Date(year, 1, 1))
    );

    if (month) {
      filteredShows = tradeShows.filter((show: TradeShowProps) => {
        const showYear = year || new Date(show?.startDate).getFullYear();

        return isSameMonth(
          new Date(show?.startDate),
          new Date(showYear, month - 1, 1)
        );
      });
    }

    setTradeShowsFilteredSorted(filteredShows);
  }, [tradeShows, year, month]);

  const onPageChange = (pageNumber: { pageNumber: number }) => {
    setCurrentPage(Number(pageNumber) - 1);
  };

  const allPastShows = () => {
    if (!tradeShows) return null;

    const filteredShows = tradeShows.filter((show: TradeShowProps) =>
      isBefore(new Date(show?.endDate), today)
    );

    setTradeShowsFilteredSorted(filteredShows);
  };

  const allUpcomingShows = () => {
    if (!tradeShows) return null;

    const filteredShows = tradeShows.filter((show: TradeShowProps) =>
      isBefore(today, new Date(show?.endDate))
    );

    setTradeShowsFilteredSorted(
      filteredShows.sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate)
      )
    );
  };

  const siblingCount = 1;
  const pageSize = 8;

  const pages: TradeShowsProps = createPages(
    tradeShowsFilteredSorted,
    pageSize
  );
  const totalCount = tradeShowsFilteredSorted
    ? tradeShowsFilteredSorted.length
    : 0;

  return (
    <StyledTradeShows id="scroll-to-anchor" ref={scrollToRef}>
      <StyledFilters>
        <StyledPastFutureFilters>
          <LinkLabel onClick={() => allPastShows()} label="All past shows" />
          <LinkLabel
            onClick={() => allUpcomingShows()}
            label="All upcoming shows"
          />
        </StyledPastFutureFilters>
        <StyledDateFilters>
          <StyledDateFiltersTitle>Select a date</StyledDateFiltersTitle>
          <StyledSelectYear
            options={years}
            label="Year"
            id="year"
            onChange={(e) =>
              setYear(!e.target.value ? null : Number(e.target.value))
            }
            placeholder="Year"
            hideLabel
          />
          <StyledSelectMonth
            options={[
              { label: 'January', value: 1 },
              { label: 'February', value: 2 },
              { label: 'March', value: 3 },
              { label: 'April', value: 4 },
              { label: 'May', value: 5 },
              { label: 'June', value: 6 },
              { label: 'July', value: 7 },
              { label: 'August', value: 8 },
              { label: 'September', value: 9 },
              { label: 'October', value: 10 },
              { label: 'November', value: 11 },
              { label: 'December', value: 12 },
            ]}
            label="Month"
            id="month"
            onChange={(e) =>
              setMonth(!e.target.value ? null : Number(e.target.value))
            }
            placeholder="Month"
            hideLabel
          />
        </StyledDateFilters>
      </StyledFilters>
      <StyledGrid>
        {pages[currentPage] &&
          pages[currentPage].length >= 1 &&
          pages[currentPage].map(
            ({
              id,
              tradeShowLink,
              logo,
              description,
              endDate,
              name,
              location,
              startDate,
            }) => (
              <StyledTradeShow key={id}>
                <StyledImageWrapper>
                  <Image src={logo?.image?.localFile} />
                </StyledImageWrapper>
                <StyledContent>
                  <Typography
                    component="h3"
                    variant="headingMedium"
                    color="brandOne100"
                  >
                    {name}
                  </Typography>
                  <Typography component="h4" variant="bodyMediumBold">
                    {format(new Date(startDate as Date), 'do LLLL yyyy')}
                    {' - '}
                    {format(new Date(endDate as Date), 'do LLLL yyyy')}
                    {', '}
                    {location}
                  </Typography>

                  <HtmlContent>{description}</HtmlContent>
                  <StyledLinkLabel
                    url={tradeShowLink}
                    label="More information"
                    rel="noreferrer nofollow"
                    target="_blank"
                  />
                </StyledContent>
              </StyledTradeShow>
            )
          )}
      </StyledGrid>
      <Pagination
        onPageChange={onPageChange}
        totalCount={totalCount || 0}
        siblingCount={siblingCount}
        currentPage={currentPage + 1}
        pageSize={pageSize}
        scrollToRef={scrollToRef}
      />
    </StyledTradeShows>
  );
};

export default TradeShows;
